﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_QUARTERS_SUCCESS, INITIAL_QUARTERS_REQUEST } from "@/redux/types/maintainers/quarters.type";

import Head from "@/components/Content/Head";
import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission, toast } from "@/services/Utils";

import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import QuartersTableHead from "./QuartersTableHead";
import QuartersTableBody from "./QuartersTableBody";
import QuartersForm from "./QuartersForm/QuartersForm";
import QuartersFilter from "./QuartersFilter";
import { filters } from "./quarters.constants";

const Quarters = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search, estate_id } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers, categories, species, varieties } = useSelector((state) => state.maintainers_quarters);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [filter, setFilter] = useState({ ...filters });
	const [showFilter, setShowFilter] = useState(false);
	const [permissions, setPermissions] = useState([]);

	const [showDelete, setShowDelete] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_QUARTERS_REQUEST });
		api.get("/admin/quarters", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, estate_id, order, orderType, paginate, page]);

	const updateData = (response) => {
		if (Object.prototype.hasOwnProperty.call(response, "quarters")) {
			const { quarters, customers, species, varieties, estates, categories, permissions: allPermissions } = response;
			dispatch({
				type: FETCH_QUARTERS_SUCCESS,
				payload: {
					quarters: quarters.data,
					last_page: quarters.last_page,
					from: quarters.from,
					to: quarters.to,
					quantity: quarters.total,
					customers: customers,
					varieties: varieties,
					estates: estates,
					categories: categories,
					species: species,
				},
			});
			setPermissions(allPermissions);
			setLoaded(true);
		} else {
			setLoaded(true);
		}
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_QUARTERS_REQUEST });
		api.post("/admin/quarters/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			toast(addToast, "Registro eliminado correctamente.", "success");
			updateData(response);
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/quarters/save", {
			...form.form,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "quarters")) {
				updateData(response);
				modalFetching(false);
				afterSave();
			} else {
				modalFetching(false);
			}
		});
	};

	const applyFilter = (params) => {
		let new_filter = { ...filter };

		if (params.customerFilter !== filter.filter_customer) {
			new_filter = {
				...new_filter,
				filter_customer: params.customerFilter,
			};
		}
		if (params.estateFilter !== filter.filter_estate) {
			new_filter = { ...new_filter, filter_estate: params.estateFilter };
		}

		if (params.categoryFilter !== filter.filter_category) {
			new_filter = {
				...new_filter,
				filter_category: params.categoryFilter,
			};
		}

		if (params.specieFilter !== filter.filter_specie) {
			new_filter = { ...new_filter, filter_specie: params.specieFilter };
		}

		if (params.varietyFilter !== filter.filter_variety) {
			new_filter = {
				...new_filter,
				filter_variety: params.varietyFilter,
			};
		}

		setFilter(new_filter);
		setShowFilter(false);
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Paso 3"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nuevo Registro"}
				action={() => navigate("/configuracion/paso-3/crear")}
				button_filter={true}
				back_url={"/configuracion?section=businessCenter"}
			/>

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					{showFilter && <QuartersFilter filter={filter} applyFilter={applyFilter} />}

					<Col md={showFilter ? 9 : 12} className={classes.main_col}>
						<Row className={classes.header}>
							<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} />
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<QuartersTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<QuartersTableBody handleDelete={handleDelete} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
					<QuartersForm
						customers={customers}
						categories={categories}
						species={species}
						varieties={varieties}
						confirmSave={confirmSave}
						permissions={permissions}
					/>
				</>
			)}
		</>
	);
};

export default Quarters;
